/**
 * Apps Context - Provides centralized app fetching and caching
 */

import { createContext, type ComponentChildren } from 'preact';
import { useContext, useEffect, useState } from 'preact/hooks';
import { query, where, orderBy, onSnapshot, collection, AppData, getDocs, db } from '../globals/firebase';
import { collectionGroup } from 'firebase/firestore';

interface AppsContextType {
  // Apps data
  apps: AppData[];
  appsLoading: boolean;
  appsError: string | null;

  // Methods
  fetchDeveloperApps: (developerId: string) => Promise<AppData[]>;
}

const AppsContext = createContext<AppsContextType | null>(null);

export function AppsProvider({ children }: { children: ComponentChildren }) {
  const [apps, setApps] = useState<AppData[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [appsError, setAppsError] = useState<string | null>(null);

  // Set up real-time listener for published apps
  useEffect(() => {
    setAppsLoading(true);
    setAppsError(null);

    // Use collection group query to get apps from all developers
    const appsQuery = query(
      collectionGroup(db, 'apps'),
      where('published', '==', true),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      appsQuery,
      (snapshot) => {
        const appsData = snapshot.docs.map(doc => {
          const data = doc.data();
          // Build canonical appId from path: developers/{username}/apps/{appName}
          const pathParts = doc.ref.path.split('/');
          const username = pathParts[1];
          const appName = pathParts[3];
          const canonicalAppId = `${username}@${appName}`;

          return {
            id: canonicalAppId,
            ...(data as Omit<AppData, 'id'>),
          };
        }) as AppData[];

        setApps(appsData);
        setAppsLoading(false);
      },
      (error) => {
        console.error('Error loading apps:', error);
        setAppsError('Failed to load apps');
        setAppsLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, []);

  const fetchDeveloperApps = async (developerId: string): Promise<AppData[]> => {
    try {
      const appsQuery = query(
        collection(db, 'apps'),
        where('developerId', '==', developerId)
      );

      const appsSnapshot = await getDocs(appsQuery);
      return appsSnapshot.docs.map((doc: any) => ({
        id: doc.id,
        ...doc.data(),
      })) as AppData[];
    } catch (err) {
      console.error('Error loading developer apps:', err);
      return [];
    }
  };

  const value: AppsContextType = {
    apps,
    appsLoading,
    appsError,
    fetchDeveloperApps,
  };

  return (
    <AppsContext.Provider value={value}>
      {children}
    </AppsContext.Provider>
  );
}

export function useApps() {
  const context = useContext(AppsContext);
  if (!context) {
    throw new Error('useApps must be used within an AppsProvider');
  }
  return context;
}